import configFile from "./config.json";

export type Environment = "development" | "staging" | "production" | "local";

export interface Config {
  portalUrl: string;
  auth0: {
    /** Auth0 tenant host the SPA authenticates against (no scheme). */
    domain: string;
    /**
     * Client ID of the Auth0 **Single Page Application**. This is not the same
     * client as the former ALB/OIDC "Regular Web Application" — PKCE requires
     * a public client with the portal origin registered as an allowed callback,
     * logout and web origin.
     */
    clientId: string;
    /**
     * API identifier of the GraphQL API in Auth0. Access tokens are only issued
     * as validatable JWTs when an audience is requested; without it Auth0
     * returns an opaque token the API cannot verify.
     */
    audience: string;
    scope: string;
    customClaims: {
      permissionsKey: string;
      rolesKey: string;
    };
  };
  google: {
    mapsAPIKey: string;
  };
  mixpanel: {
    /**
     * Project token for the portal's *own* Mixpanel project in this
     * environment — deliberately separate from the Concierge App projects so
     * portal activity never mixes into concierge-app/concierge-web funnels.
     *
     * Values beginning with `PLACEHOLDER` have not been provisioned yet: the
     * analytics module treats them like an empty token and stays disabled.
     * Replace them with the real tokens from Mixpanel → Project Settings.
     */
    token: string;
  };
  aws: {
    region: string;
    s3: {
      baseUrl: string;
    };
    // CloudWatch RUM is deliberately absent: the CDK app creates the monitor
    // and its identity pool, so their IDs only exist once deployed — after this
    // bundle is built. They are published as runtime-config.json and read at
    // start-up by src/utils/awsrum.tsx.
    graphql: {
      url: string;
    };
    assets: {
      domain: string;
    };
  };
}

// Baked in at build time by vite.config.ts. There is no server process to read
// environment variables from, so anything not resolved here is not available.
export const env: Environment = import.meta.env.VITE_APP_ENV || "development";
export const isLocal = env === "local";
export const isProduction = env === "production";

// Previously read from LOG_LEVEL on the Node container. A static bundle has no
// such knob, so the level is derived from the build target instead.
export const logLevel = isLocal ? "debug" : isProduction ? "error" : "info";

export default function getAppConfig() {
  return configFile[env] as Config;
}
